import { plural } from "@amber/contracts";
import { AppLink, Avatar, PopoverHeading, StatusPopover, formatCount } from "@amber/ui";
import type { HomeResponse } from "@amber/application/contracts";
import { FRAME_DAYS, FRAME_TEXT } from "./format";
import { Count, Panel } from "./parts";

type Person = HomeResponse["people"]["rows"][number];

function Bars({ values, frameDays, label }: { values: number[]; frameDays: number; label: string }) {
  const max = Math.max(1, ...values);
  return (
    <svg role="img" aria-label={label} width={90} height={18} viewBox="0 0 90 18" className="flex-none">
      {values.map((v, i) => (
        <rect
          key={i}
          x={i * 3}
          y={18 - Math.max(1, (v / max) * 18)}
          width={2}
          height={Math.max(1, (v / max) * 18)}
          className={i >= values.length - frameDays ? "fill-link" : "fill-surface-3"}
        />
      ))}
    </svg>
  );
}

function PersonSources({ p }: { p: Person }) {
  if (p.sources.active === 0) return <span className="text-muted">No active sources</span>;
  return (
    <StatusPopover
      label={
        <span>
          {`${formatCount(p.sources.delivering)} of ${formatCount(p.sources.active)}`}{" "}
          <span className={p.sources.problems.length > 0 ? "text-waiting" : "text-muted"}>· {p.sources.summary}</span>
        </span>
      }
    >
      <PopoverHeading>{p.name}: sources</PopoverHeading>
      {p.sources.problems.length === 0 ? (
        <p>All active sources are delivering.</p>
      ) : (
        p.sources.problems.map((s) => (
          <p key={s.title}>{`${s.title} · ${s.state}${s.reason ? ` · ${s.reason}` : ""}`}</p>
        ))
      )}
      <AppLink href={`/profiles/${p.id}?tab=sources`} className="text-link">
        Open the profile →
      </AppLink>
    </StatusPopover>
  );
}

function PersonRow({ p, frameDays }: { p: Person; frameDays: number }) {
  return (
    <tr className="border-t border-divider hover:bg-row-hover">
      <td className="px-4 py-1.5">
        <AppLink href={`/profiles/${p.id}`} className="flex items-center gap-2 text-fg no-underline hover:underline">
          <Avatar name={p.name} size="md" />
          <span className="min-w-0">
            <span className="block font-strong">{p.name}</span>
            {p.role ? <span className="block truncate text-sm text-muted">{p.role}</span> : null}
          </span>
        </AppLink>
      </td>
      <td className="px-2 py-1.5">
        <span className="flex items-center gap-2">
          <b className="w-6 font-mono font-semibold">
            <Count href={p.eligible.href} count={p.eligible.count} team={false} label={`${p.name} eligible`} />
          </b>
          <Bars
            values={p.daily}
            frameDays={frameDays}
            label={`${p.name}: eligible communications per day over 30 days; ${formatCount(p.eligible.count)} in the time frame`}
          />
        </span>
      </td>
      <td className="px-2 py-1.5">
        <PersonSources p={p} />
      </td>
      <td className="px-2 py-1.5 max-narrow:hidden">
        {p.required.length === 0 ? (
          <span className="text-muted">—</span>
        ) : (
          p.required.map((r) => (
            <div key={r.title} className={r.ok ? "" : "text-waiting"}>{`${r.ok ? "✓ " : ""}${r.text}`}</div>
          ))
        )}
      </td>
      <td className="px-4 py-1.5 text-right font-mono">
        {p.open_gaps.count === 0 ? (
          <span className="text-muted">0</span>
        ) : (
          <Count href={p.open_gaps.href} count={p.open_gaps.count} team={false} label={`${p.name} open gaps`} />
        )}
      </td>
    </tr>
  );
}

export function PeoplePanel({ data }: { data: HomeResponse }) {
  const rows = data.people.rows;
  const frameDays = FRAME_DAYS[data.frame];
  return (
    <Panel
      labelledBy="home-people"
      title="People"
      lead={`Tracked people · eligible in the ${FRAME_TEXT[data.frame].toLowerCase()}`}
      link={{ href: "/profiles", label: "All profiles" }}
    >
      <div className="-mx-4 overflow-x-auto">
        <table className="w-full border-collapse text-md">
          <thead className="bg-surface text-muted">
            <tr>
              <th className="px-4 py-1.5 text-left font-normal">Person</th>
              <th className="px-2 py-1.5 text-left font-normal">Eligible</th>
              <th className="px-2 py-1.5 text-left font-normal">Sources delivering</th>
              <th className="px-2 py-1.5 text-left font-normal max-narrow:hidden">Required routes</th>
              <th className="px-4 py-1.5 text-right font-normal">Open gaps</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <PersonRow key={p.id} p={p} frameDays={frameDays} />
            ))}
          </tbody>
        </table>
      </div>
      {data.people.more > 0 ? (
        <p className="m-0 mt-2 text-md">
          <AppLink href="/profiles" className="text-link">
            {plural(data.people.more, "more", { format: formatCount })} in Profiles
          </AppLink>
        </p>
      ) : null}
      <p className="m-0 mt-2 text-sm text-muted">
        A communication with two tracked people counts for each of them whose contributions are Eligible; paused sources
        count neither as delivering nor as not delivering.
      </p>
    </Panel>
  );
}
